import { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';

// Employee self-service onboarding ("İşe Alıştırmam").
// Mirrors the mobile onboarding screens against /v1/my-onboarding.
// GetHome returns { Status:true, Data:null } when there's no active onboarding
// (empty state, no toast); sub-endpoints only 404 when none exists, so we gate
// their fetch on home having data.
export default function useMyOnboardingApi() {
  const [loading, setLoading] = useState(true);
  const [home, setHome] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [goals, setGoals] = useState([]);
  const [checkIns, setCheckIns] = useState([]);
  const [oneOnOnes, setOneOnOnes] = useState([]);
  const [periodApprovals, setPeriodApprovals] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    (async () => {
      const homeRes = await api.get('/v1/my-onboarding');
      const homeData = homeRes?.data?.data || null;
      if (cancelled) return;

      setHome(homeData);

      if (!homeData) {
        setTasks([]);
        setGoals([]);
        setCheckIns([]);
        setOneOnOnes([]);
        setPeriodApprovals([]);
        setLoading(false);
        return;
      }

      const [tasksRes, goalsRes, checkInsRes, oooRes, paRes] = await Promise.all([
        api.get('/v1/my-onboarding/tasks'),
        api.get('/v1/my-onboarding/goals'),
        api.get('/v1/my-onboarding/check-ins'),
        api.get('/v1/my-onboarding/one-on-ones'),
        api.get('/v1/my-onboarding/period-approvals'),
      ]);
      if (cancelled) return;

      setTasks(unwrapList(tasksRes));
      setGoals(unwrapList(goalsRes));
      setCheckIns(unwrapList(checkInsRes));
      setOneOnOnes(unwrapList(oooRes));
      setPeriodApprovals(unwrapList(paRes));
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [refreshTrigger]);

  const startTask = useCallback(async (taskId) => {
    try { await api.post(`/v1/my-onboarding/tasks/${taskId}/start`); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const completeTask = useCallback(async (taskId, notesMarkdown) => {
    try { await api.post(`/v1/my-onboarding/tasks/${taskId}/complete`, { notesMarkdown: notesMarkdown || null }); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const agreeGoal = useCallback(async (goalId) => {
    try { await api.post(`/v1/my-onboarding/goals/${goalId}/agree`); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const acknowledgePeriod = useCallback(async (periodApprovalId) => {
    try { await api.post(`/v1/my-onboarding/period-approvals/${periodApprovalId}/acknowledge`); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  const acknowledgeOneOnOne = useCallback(async (oneOnOneId, reflectionMarkdown) => {
    try { await api.post(`/v1/my-onboarding/one-on-ones/${oneOnOneId}/acknowledge`, { reflectionMarkdown: reflectionMarkdown || null }); refresh(); return true; }
    catch { return false; }
  }, [refresh]);

  return {
    loading, home, tasks, goals, checkIns, oneOnOnes, periodApprovals,
    refresh, startTask, completeTask, agreeGoal, acknowledgePeriod, acknowledgeOneOnOne,
  };
}

function unwrapList(res) {
  const payload = res?.data?.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
}
